interface QueueTask {
    priority: number;
    run: (signal: AbortSignal) => Promise<void>;
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: unknown) => void;
    controller: AbortController;
    active: boolean;
}

/** 图集页队列：后台最多三路，为展示目标保留第四路。 */
export class ClipQueue {
    private tasks = new Map<string, QueueTask>();
    private active = 0;

    request(key: string, priority: number, run: (signal: AbortSignal) => Promise<void>): Promise<void> {
        const existing = this.tasks.get(key);
        if (existing) {
            existing.priority = Math.min(existing.priority, priority);
            this.pump();
            return existing.promise;
        }
        let resolve!: () => void;
        let reject!: (error: unknown) => void;
        const promise = new Promise<void>((ok, fail) => { resolve = ok; reject = fail; });
        this.tasks.set(key, { priority, run, promise, resolve, reject, controller: new AbortController(), active: false });
        this.pump();
        return promise;
    }

    cancelWhere(predicate: (key: string, priority: number) => boolean) {
        for (const [key, task] of this.tasks) {
            if (!predicate(key, task.priority)) continue;
            this.tasks.delete(key);
            task.controller.abort();
            task.reject(new DOMException("Resource request cancelled", "AbortError"));
        }
        this.pump();
    }

    private pump() {
        while (this.active < 4) {
            const next = [...this.tasks.entries()]
                .filter(([, task]) => !task.active && (task.priority < 2 || this.active < 3))
                .sort(([, a], [, b]) => a.priority - b.priority)[0];
            if (!next) return;
            const [key, task] = next;
            task.active = true;
            this.active++;
            void (async () => {
                try {
                    await task.run(task.controller.signal);
                    task.resolve();
                } catch (error) {
                    task.reject(error);
                } finally {
                    this.active--;
                    if (this.tasks.get(key) === task) this.tasks.delete(key);
                    this.pump();
                }
            })();
        }
    }
}
